import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Box, Button } from "@mui/material";
import { keyframes } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import admobService from "native/admob";
import { FaMedal } from "react-icons/fa6";
import CurrentSession from "data/userSession";
import useCustomNavigation from "hooks/customNavigation";
import { APP_IDENTITY } from "config/app";

const subtlePulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
`;

const AdBanner = () => {
  const theme = useTheme();
  const navigate = useCustomNavigation();
  const hasInitialized = useRef(false);
  const [supporter, setSupporter] = useState(true);
  const isNative = Capacitor.isNativePlatform();

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    CurrentSession.load().then((user) => {
      const isDemoUser = user?.uid === APP_IDENTITY.demoUserId;
      setSupporter(isDemoUser ? false : !!user?.supporter);
      setLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!loaded || !isNative) {
      return;
    }

    // If supporter, actively remove any existing native ad overlay
    if (supporter) {
      admobService.hideBannerAd();
      return;
    }

    // Add CSS to position toasts above ad banner (only once)
    if (!document.getElementById("ad-banner-toast-styles")) {
      const toastStyleElement = document.createElement("style");
      toastStyleElement.id = "ad-banner-toast-styles";
      toastStyleElement.textContent = `
        .notistack-SnackbarContainer,
        .MuiSnackbar-root,
        [class*="SnackbarContainer"],
        [class*="snackbar-container"] {
          bottom: 75px !important;
        }

        .notistack-Snackbar,
        .MuiSnackbar-root,
        [class*="Snackbar"],
        [class*="snackbar"] {
          bottom: 75px !important;
        }

        .notistack-SnackbarContainer,
        .MuiSnackbar-root {
          z-index: 9999 !important;
        }
      `;
      document.head.appendChild(toastStyleElement);
    }

    // Show ad once
    const initAd = async () => {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        // Wait a bit for the app to settle
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await admobService.showBannerAd();
      }
    };

    initAd();

    // Listen for app state changes to restore ad if needed
    // The service now handles skipping the call if already loaded
    const appStateListener = CapacitorApp.addListener(
      "appStateChange",
      async (state) => {
        if (state.isActive) {
          await admobService.showBannerAd();
        }
      }
    );

    // Cleanup: Only remove listeners, don't hide the ad
    return () => {
      appStateListener.then((listener) => listener.remove());
    };
  }, [loaded, supporter, isNative]);

  // Don't render for supporters
  if (supporter) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        height: isNative ? "66px" : "50px",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar + 1,
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
      }}
    >
      <Button
        startIcon={<FaMedal />}
        onClick={() => {
          navigate("support");
        }}
        color="warning"
        variant="contained"
        size="small"
        sx={{
          animation: `${subtlePulse} 3s ease-in-out infinite`,
        }}
      >
        Support this app to keep it running!
      </Button>
    </Box>
  );
};

export default AdBanner;
